import { QueryClient, type VueQueryPluginOptions } from '@tanstack/vue-query'
import { ApmpApiError } from '~/shared/api/errors'
import { staleTimes } from '~/shared/api/query-keys'

/**
 * The app's query behaviour, exported so a test can build a **fresh** client with the same
 * rules. Tests that let TanStack's defaults apply retry a deliberate 404 three times with
 * exponential backoff, which turns "renders not found" into a timeout rather than a failure —
 * and a test asserting against different retry behaviour from production is not asserting much.
 */
export const queryDefaults: NonNullable<
  ConstructorParameters<typeof QueryClient>[0]
>['defaultOptions'] = {
  queries: {
    staleTime: staleTimes.list,
    refetchOnWindowFocus: true,
    retry: (failureCount: number, error: unknown) => {
      // Never retry a deliberate refusal — 4xx means the request was wrong, and
      // retrying just burns the user's rate limit.
      if (error instanceof ApmpApiError && error.status >= 400 && error.status < 500) {
        return false
      }
      return failureCount < 2
    },
  },
  mutations: {
    // A mutation retried blindly can double-charge. Retries are opt-in per mutation,
    // and only with an idempotency key (apmp-money-safety).
    retry: false,
  },
}

/**
 * One QueryClient, created here rather than by the plugin, because logout has to be able to
 * `clear()` it: a cache that survives a user switch will render the previous user's data.
 */
export const queryClient = new QueryClient({ defaultOptions: queryDefaults })

export const queryClientConfig: VueQueryPluginOptions = { queryClient }
